import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TmCart } from '@/tscapi/model/cart';
import { TmProduct } from '@/tscapi/model/product';
import { Cookie } from '@/tscapi/utils/cookie';

import { useAuthenticated } from './hooks/useAuthenticated';
import { useAddItem } from './hooks/useCart';
import { useCartData } from './hooks/useCartData';
import { useDonation } from './hooks/useDonation';
import { en, fr } from './i18n';

interface DonationSelectorProps {
    isCartEmpty: boolean;
    isOnlyDonationProductInCart: boolean;
    data: TmCart;
}

const DonationSelector = ({ isCartEmpty, isOnlyDonationProductInCart, data }: DonationSelectorProps) => {
    const cookie: Cookie = new Cookie();
    const isDonationFlowEnabled = window['donationFlowEnabled'];
    const { data: dataDonation, isLoading } = useDonation();
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const { donationProduct } = useCartData(data);
    const mutation = useAddItem(cookie.UserAccessToken?.Edp, cookie.CartGuid);
    const [products, setProducts] = useState<TmProduct[]>([]);
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);

    const onDonationChange = (edpNo) => {
        //do react-query mutation
        mutation.mutate({ itemEDP: edpNo, Quantity: 1 });
    };

    useEffect(() => {
        if (isCartEmpty || isOnlyDonationProductInCart) {
            setIsPanelOpen(true);
        } else {
            setIsPanelOpen(false);
        }
    }, [isCartEmpty, isOnlyDonationProductInCart]);

    useEffect(() => {
        //retrieve donation products, make a copy before we modify price formatting
        const products = Object.assign([], dataDonation);
        if (typeof dataDonation != 'undefined' && dataDonation?.length > 0) {
            //strip currency formatting and decimals
            products.forEach(function (item) {
                item.IsPriceRange = item.IsPriceRange.replace('$', '');
                item.IsPriceRange = Math.trunc(parseFloat(item.IsPriceRange)).toString();
            }, this);

            //sort from smallest to largest donations
            products.sort(function (a, b) {
                return parseFloat(a.IsPriceRange) > parseFloat(b.IsPriceRange)
                    ? 1
                    : parseFloat(b.IsPriceRange) > parseFloat(a.IsPriceRange)
                      ? -1
                      : 0;
            });

            //add currency formatting
            products.forEach(function (item, index) {
                if (cookie.Language.toLowerCase() == 'en_ca') {
                    item.IsPriceRange = '$' + item.IsPriceRange;
                } else {
                    item.IsPriceRange = item.IsPriceRange + ' $';
                }
            }, this);
            setProducts(products);
        }
    }, [dataDonation, cookie.Language]);

    if (isLoading) {
        return <>Donation Loading...</>;
    }

    return (
        <>
            {isDonationFlowEnabled && products && (
                <>
                    <div className="donation__wrap">
                        <button
                            className="donation__card"
                            type="button"
                            aria-label="Open Donation Panel"
                            onClick={() => {
                                setIsPanelOpen(!isPanelOpen);
                            }}
                        >
                            <div className="donation__image">
                                <img
                                    alt={t('Jays Care')}
                                    width="75"
                                    height="36"
                                    src="/tsc/en_CA/Assets/images/jayscare.png"
                                />
                            </div>
                            <div className="donation__label">
                                <span>{t('Would you like to donate to the Jays Care Foundation?')}</span>
                            </div>
                            <div
                                className={classNames('donation__icon', {
                                    'icon-plus': !isPanelOpen,
                                    'icon-minus': isPanelOpen,
                                })}
                            ></div>
                        </button>
                        {isPanelOpen && (
                            <>
                                <div className="donation__message">
                                    {t(
                                        'Donate today to teach life skills and create lasting social change for Canadian children and youth in marginalized communities.',
                                    )}
                                </div>
                                <div className="donation__selector">
                                    {products.map((product, index) => (
                                        <div key={index}>
                                            <input
                                                id={index.toString()}
                                                className="donation__selector--radio"
                                                type="radio"
                                                value={product?.Edps[0].EdpNo}
                                                name={'donation selector'}
                                                title={product?.Edps[0].AppliedPrice}
                                                checked={product?.Edps[0].EdpNo == donationProduct?.CartLineItem?.EdpNo}
                                                onChange={() => onDonationChange(product?.Edps[0].EdpNo)}
                                            />
                                            <label htmlFor={index.toString()} className="donation__selector--label">
                                                <span>{product?.IsPriceRange}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div className="donation__sub-message">
                                    {t(
                                        'The Jays Care Foundation will issue a donation receipt to the credit card holder for amounts in excess of $20',
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default DonationSelector;
